import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Home, Calendar, Clock } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import contentPagesService from '../services/contentPages';

export default function PublicContentPage() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        fetchPage();
        window.scrollTo(0, 0);
    }, [slug]);

    const fetchPage = async () => {
        try {
            setLoading(true);
            setError(false);
            const data = await contentPagesService.getBySlug(slug);
            setPage(data);

            // Set metadata
            if (data.meta_title) document.title = data.meta_title;
            else document.title = `${data.title} | eDisplayroom`;

        } catch (err) {
            console.error("Error fetching page:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col pt-20">
                <Header />
                <div className="flex-grow flex items-center justify-center">
                    <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error || !page) {
        return (
            <div className="min-h-screen flex flex-col pt-20">
                <Header />
                <div className="flex-grow flex flex-col items-center justify-center p-6 space-y-4 text-center">
                    <h1 className="text-4xl font-bold text-slate-900 uppercase tracking-tighter italic">Page Not Found</h1>
                    <p className="text-slate-500 max-w-md">Sorry, but the page you are looking for does not exist or has been removed.</p>
                    <Link to="/" className="px-6 py-2 bg-amber-600 text-white font-bold rounded-full hover:bg-amber-700 transition-colors uppercase text-sm tracking-wider">
                        Back to Home
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col pt-20 bg-white">
            <Header />

            {/* Breadcrumbs */}
            <div className="bg-slate-50 border-b border-slate-100 py-3">
                <div className="max-w-4xl mx-auto px-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                    <Link to="/" className="hover:text-amber-700 flex items-center gap-1">
                        <Home size={12} /> Home
                    </Link>
                    <ChevronRight size={14} />
                    <span className="text-slate-900">{page.title}</span>
                </div>
            </div>

            <main className="flex-grow container max-w-4xl mx-auto px-6 py-12 lg:py-20">
                <article className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <header className="space-y-4 border-b border-slate-100 pb-10">
                        <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight italic uppercase">
                            {page.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400 font-medium">
                            <div className="flex items-center gap-2">
                                <Calendar size={16} className="text-amber-600" />
                                Last updated: {new Date(page.updated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                            </div>
                            <div className="hidden sm:flex items-center gap-2">
                                <Clock size={16} className="text-amber-600" />
                                {Math.ceil(page.content.split(' ').length / 200)} min read
                            </div>
                        </div>
                    </header>

                    <div
                        className="prose prose-slate lg:prose-lg max-w-none 
                prose-headings:font-bold prose-headings:text-slate-900 prose-headings:uppercase prose-headings:italic
                prose-p:text-slate-600 prose-p:leading-relaxed
                prose-a:text-amber-700 prose-a:font-bold prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-2xl prose-img:shadow-lg prose-img:border prose-img:border-slate-100
                quill-content"
                        dangerouslySetInnerHTML={{ __html: page.content }}
                    />
                </article>
            </main>

            <Footer />

            <style jsx>{`
        .quill-content :global(h1), .quill-content :global(h2), .quill-content :global(h3) {
            margin-top: 2em;
            margin-bottom: 0.5em;
        }
        .quill-content :global(p) {
            margin-bottom: 1.5em;
        }
        .quill-content :global(ul), .quill-content :global(ol) {
            margin-bottom: 1.5em;
            padding-left: 1.5em;
        }
        .quill-content :global(li) {
            margin-bottom: 0.5em;
        }
        .quill-content :global(img) {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 2em auto;
        }
        .quill-content :global(blockquote) {
            border-left: 4px solid #d97706;
            padding-left: 1.5em;
            font-style: italic;
            color: #475569;
            margin: 2em 0;
        }
      `}</style>
        </div>
    );
}
